import {
  CategoryMaster,
  ItemMaster,
  TypeMaster,
} from '../../../services/apiTypes';
import {
  MANAGE_ITEM_MODE,
  MANAGE_ITEM_ROW_TYPE,
} from '../../../_constants/manageConst';
import { arrayMove, useSortable } from '@dnd-kit/sortable';
import { TableRow } from '@mui/material';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  LEAK_TEST,
  VARIABLE_HARDNESS,
} from '../../../_constants/inspectionConst';
import { theme } from '../../../theme';
import { manageItemStyle } from '../components/InspectionItemTable';
import { CSS } from '@dnd-kit/utilities';

type useSortItemProps = {
  categoryMaster: CategoryMaster[];
  setCategoryMaster: React.Dispatch<React.SetStateAction<CategoryMaster[]>>;
  typeMaster: TypeMaster[];
  setTypeMaster: React.Dispatch<React.SetStateAction<TypeMaster[]>>;
  itemMaster: ItemMaster[];
  setItemMaster: React.Dispatch<React.SetStateAction<ItemMaster[]>>;
  targetItem: ItemMaster;
  displayMode: number;
  isOpenAccordions?: Record<string, boolean>;
  handleAccordion?: (id: string) => void;
  isSetPFRCodes: (item: ItemMaster) => boolean;
};

const useSortItem = ({
  categoryMaster,
  setCategoryMaster,
  typeMaster,
  setTypeMaster,
  itemMaster,
  setItemMaster,
  targetItem,
  displayMode,
  isOpenAccordions,
  handleAccordion,
  isSetPFRCodes,
}: useSortItemProps) => {
  /** 並び替え用のTableRowを作成する */
  const SortableItem = (props: any) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: props.id,
        disabled: !(displayMode === MANAGE_ITEM_MODE.SORT),
      });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    // 小項目のハイライト
    let isHighlights = false;
    if (props.name === MANAGE_ITEM_ROW_TYPE.ITEM) {
      const item = itemMaster.find((i) => i.itemId === props.id);
      if (item) {
        if (item.itemId === targetItem.itemId) {
          // 編集中の項目はハイライトしない
        } else if (item.itemId === LEAK_TEST.dryLeakTestItemId) {
          // 乾式気密検査はハイライトしない
        } else if (
          item.typeId === VARIABLE_HARDNESS.typeId &&
          item.itemId !== VARIABLE_HARDNESS.itemId
        ) {
          // 硬度可変測定の差分以外はハイライトしない
        } else if (
          !itemMaster.find((i) => i.parentItemId === item.itemId) &&
          !isSetPFRCodes(item)
        ) {
          // 子要素がなく、PFRが選択されていなければハイライトする
          isHighlights = true;
        }
      }
    }

    return (
      <TableRow
        key={props.id}
        ref={setNodeRef}
        style={style}
        sx={{
          '&:hover, &:hover td': {
            backgroundColor: theme.palette.secondary.light,
          },
          '&, & td': {
            backgroundColor: isHighlights ? '#FDEEE6' : '',
          },
          borderTop:
            props.name === MANAGE_ITEM_ROW_TYPE.CATEGORY
              ? manageItemStyle.doubleBorder
              : 'initial',
        }}
        {...attributes}
        {...listeners}
        id={props.id}
      >
        {props.children}
      </TableRow>
    );
  };

  /** 並び替え開始時、並び替え対象の項目が開いていれば閉じる */
  const handleDragStart = (event: DragStartEvent) => {
    if (!isOpenAccordions || !handleAccordion) {
      return;
    }
    const { active } = event;
    active.data.current?.sortable.items.map((row: string) => {
      if (isOpenAccordions[row] && typeof row === 'string') {
        handleAccordion(row);
      }
    });
  };

  /** 並び替え後、移動可能か判定する */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const masterType = active.data.current?.sortable.containerId;
    if (
      active.id !== over?.id &&
      masterType === over?.data.current?.sortable.containerId
    ) {
      // 大項目の移動
      if (masterType === MANAGE_ITEM_ROW_TYPE.CATEGORY) {
        const fromCategoryIndex = categoryMaster.findIndex(
          (row) => row.categoryId === active.id,
        );
        const toCategoryIndex = categoryMaster.findIndex(
          (row) => row.categoryId === over!.id,
        );
        // 移動前のindexから移動先のindexへ移動する
        const categories = arrayMove(
          categoryMaster,
          fromCategoryIndex,
          toCategoryIndex,
        );
        setCategoryMaster(categories);
        return;
      }
      // 中項目の移動
      if (masterType === MANAGE_ITEM_ROW_TYPE.TYPE) {
        const fromTypeIndex = typeMaster.findIndex(
          (row) => row.typeId === active.id,
        );
        const toTypeIndex = typeMaster.findIndex(
          (row) => row.typeId === over!.id,
        );
        // 同じ大項目内でのみ移動可能
        if (
          typeMaster[fromTypeIndex].categoryId ===
          typeMaster[toTypeIndex].categoryId
        ) {
          // 移動前のindexから移動先のindexへ移動する
          const types = arrayMove(typeMaster, fromTypeIndex, toTypeIndex);
          setTypeMaster(types);
        }
        return;
      }
      // 小項目の移動
      if (masterType === MANAGE_ITEM_ROW_TYPE.ITEM) {
        // itemIdからindex抽出
        const fromItemIndex = itemMaster.findIndex(
          (row) => row.itemId === active.id,
        );
        const toItemIndex = itemMaster.findIndex(
          (row) => row.itemId === over!.id,
        );
        // 中項目、階層の違う移動は不可
        if (
          itemMaster[fromItemIndex].typeId !== itemMaster[toItemIndex].typeId ||
          itemMaster[fromItemIndex].depth !== itemMaster[toItemIndex].depth
        ) {
          return;
        }
        // 親階層を跨いだ移動は不可
        if (
          itemMaster[fromItemIndex].depth > 0 &&
          itemMaster[fromItemIndex].parentItemId !==
            itemMaster[toItemIndex].parentItemId
        ) {
          return;
        }
        if (itemMaster[fromItemIndex].depth < 2) {
          // 移動する項目とその子項目を全て取得する
          const fromItem = itemMaster[fromItemIndex];
          const fromItemList: string[] = [fromItem.itemId];
          itemMaster.map((item1) => {
            if (item1.parentItemId === fromItem.itemId) {
              fromItemList.push(item1.itemId);
              itemMaster.map((item2) => {
                if (item2.parentItemId === item1.itemId) {
                  fromItemList.push(item2.itemId);
                }
              });
            }
          });
          // 移動先の子項目を全て取得する
          const toItem = itemMaster[toItemIndex];
          const toItemList: string[] = [toItem.itemId];
          itemMaster.map((item1) => {
            if (item1.parentItemId === toItem.itemId) {
              toItemList.push(item1.itemId);
              itemMaster.map((item2) => {
                if (item2.parentItemId === item1.itemId) {
                  toItemList.push(item2.itemId);
                }
              });
            }
          });
          // 移動元の小項目を下の階層分含めて移動させる
          const items = [...itemMaster];
          const moveItems = items.splice(fromItemIndex, fromItemList.length);
          const newIndex =
            fromItemIndex < toItemIndex
              ? items.findIndex(
                  (row) => row.itemId === itemMaster[toItemIndex].itemId,
                ) + toItemList.length
              : toItemIndex;
          items.splice(newIndex, 0, ...moveItems);
          setItemMaster(items);
        } else {
          // 階層2は下層を気にせず入れ替える
          const items = arrayMove(itemMaster, fromItemIndex, toItemIndex);
          setItemMaster(items);
        }
      }
    }
  };

  return {
    SortableItem,
    handleDragStart,
    handleDragEnd,
  };
};

export default useSortItem;
